
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Line2} from "../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../libs/three.js/lines/LineMaterial.js";
import {TextSprite} from "../TextSprite.js";
import {Utils} from "../utils.js";
import {EventDispatcher} from "../EventDispatcher.js";

export class LandmarkDistanceTool extends EventDispatcher {

	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.scene = new THREE.Scene();
		this.scene.name = "scene_landmark_distances";
		this.viewer.inputHandler.registerInteractiveScene(this.scene);

		this.distanceGroup = new Set();
		this.visible = true;
		this.edges = [];
		this.endpointDots = [];
		this.referencePoint = new THREE.Vector3();

		this._pendingRebuildTimer = null;

		viewer.addEventListener("update", this.update.bind(this));
		viewer.addEventListener("render.pass.perspective_overlay", this.render.bind(this));
	}

	toggleInGroup(zone) {
		if (this.distanceGroup.has(zone)) {
			this.distanceGroup.delete(zone);
		} else {
			this.distanceGroup.add(zone);
		}
		this.rebuildMST();
		this.dispatchEvent({type: "group_changed", group: this.distanceGroup});
	}

	isInGroup(zone) {
		return this.distanceGroup.has(zone);
	}

	clearGroup() {
		this.distanceGroup.clear();
		this.clearScene();
		if (this._pendingRebuildTimer) {
			clearTimeout(this._pendingRebuildTimer);
			this._pendingRebuildTimer = null;
		}
		this.dispatchEvent({type: "group_changed", group: this.distanceGroup});
	}

	setVisible(bool) {
		this.visible = bool;
		this.scene.visible = bool;
	}

	clearScene() {
		while (this.scene.children.length > 0) {
			this.scene.remove(this.scene.children[0]);
		}
		this.edges = [];
		this.endpointDots = [];
	}

	// Schedule a single deferred rebuild to pick up newly loaded nodes
	_scheduleDeferredRebuild() {
		if (this._pendingRebuildTimer) {
			return;
		}
		this._pendingRebuildTimer = setTimeout(() => {
			this._pendingRebuildTimer = null;
			if (this.distanceGroup.size >= 2) {
				this.rebuildMST();
			}
		}, 2000);
	}

	rebuildMST() {
		this.clearScene();

		if (this.distanceGroup.size < 2) {
			return;
		}

		// Recompute centroids by traversing the full octree.
		// If any overlapping nodes are still loading, schedule a deferred rebuild.
		let pointclouds = this.viewer.scene.pointclouds;
		let allLoaded = true;
		for (let zone of this.distanceGroup) {
			let loaded = zone.computeLidarCentroid(pointclouds);
			if (!loaded) {
				allLoaded = false;
			}
		}

		if (!allLoaded) {
			this._scheduleDeferredRebuild();
		}

		// Collect nodes with centroids
		let nodes = [];
		for (let zone of this.distanceGroup) {
			if (!zone.lidarCentroid) {
				continue;
			}
			nodes.push({zone, centroid: zone.lidarCentroid});
		}

		if (nodes.length < 2) {
			return;
		}

		// Compute reference point (average of all centroids) for float32 precision
		this.referencePoint.set(0, 0, 0);
		for (let node of nodes) {
			this.referencePoint.add(node.centroid);
		}
		this.referencePoint.divideScalar(nodes.length);

		// Run MST
		let mstEdges = this.computeKruskalMST(nodes);

		// Build visuals for each edge
		for (let edge of mstEdges) {
			let nodeA = nodes[edge.i];
			let nodeB = nodes[edge.j];
			let cA = nodeA.centroid;
			let cB = nodeB.centroid;

			// Positions relative to reference point
			let localA = cA.clone().sub(this.referencePoint);
			let localB = cB.clone().sub(this.referencePoint);

			// Line2 with dashed Cyvl Green material
			let lineGeometry = new LineGeometry();
			lineGeometry.setPositions([
				localA.x, localA.y, localA.z,
				localB.x, localB.y, localB.z,
			]);

			let lineMaterial = new LineMaterial({
				color: 0xDAFF00,
				linewidth: 2,
				dashed: true,
				dashSize: 5,
				gapSize: 3,
				depthTest: false,
			});
			lineMaterial.resolution.set(
				this.viewer.renderer.domElement.clientWidth,
				this.viewer.renderer.domElement.clientHeight
			);

			let line = new Line2(lineGeometry, lineMaterial);
			line.position.copy(this.referencePoint);
			line.computeLineDistances();
			this.scene.add(line);

			// Label at midpoint
			let distanceFt = edge.distance * 3.28084;
			let label = new TextSprite(`${distanceFt.toFixed(2)} ft`);
			label.setBorderColor({r: 218, g: 255, b: 0, a: 0.0});
			label.setBackgroundColor({r: 0, g: 0, b: 0, a: 0.5});
			label.material.depthTest = false;
			label.material.depthWrite = false;
			label.material.transparent = true;

			let midpoint = new THREE.Vector3().addVectors(cA, cB).multiplyScalar(0.5);
			let localMid = midpoint.clone().sub(this.referencePoint);

			// Group to hold label, positioned at reference point
			let labelGroup = new THREE.Group();
			labelGroup.position.copy(this.referencePoint);
			label.position.copy(localMid);
			labelGroup.add(label);
			this.scene.add(labelGroup);

			// Invisible sphere at midpoint for click targeting
			let sphereRadius = edge.distance * 0.05;
			sphereRadius = Math.max(sphereRadius, 0.5);
			let sphereGeometry = new THREE.SphereGeometry(sphereRadius, 8, 8);
			let sphereMaterial = new THREE.MeshBasicMaterial({
				visible: false,
			});
			let midpointSphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
			midpointSphere.position.copy(midpoint);
			midpointSphere.userData.landmarkEdge = {
				zoneA: nodeA.zone,
				zoneB: nodeB.zone,
				distance: edge.distance,
			};

			// Required for InputHandler picking
			midpointSphere.addEventListener('select', (e) => {});
			midpointSphere.addEventListener('deselect', (e) => {});

			this.scene.add(midpointSphere);

			this.edges.push({
				zoneA: nodeA.zone,
				zoneB: nodeB.zone,
				line,
				label,
				midpointSphere,
				distance: edge.distance,
			});
		}

		// Add endpoint dots at each centroid
		let dotMaterial = new THREE.MeshBasicMaterial({
			color: 0xDAFF00,
			depthTest: false,
		});
		let dotGeometry = new THREE.SphereGeometry(1, 12, 12);
		let outlineMaterial = new THREE.MeshBasicMaterial({
			color: 0x000000,
			depthTest: false,
			side: THREE.BackSide,
		});

		for (let node of nodes) {
			let dotGroup = new THREE.Group();
			dotGroup.position.copy(node.centroid);
			dotGroup.renderOrder = 1;

			let dot = new THREE.Mesh(dotGeometry, dotMaterial);
			dotGroup.add(dot);

			// Slightly larger black sphere behind for thin border effect
			let outline = new THREE.Mesh(dotGeometry, outlineMaterial);
			outline.scale.set(1.12, 1.12, 1.12);
			dotGroup.add(outline);

			this.scene.add(dotGroup);
			this.endpointDots.push(dotGroup);
		}
	}

	// Kruskal's MST with Union-Find
	computeKruskalMST(nodes) {
		let n = nodes.length;

		// Build all-pairs edges
		let edges = [];
		for (let i = 0; i < n; i++) {
			for (let j = i + 1; j < n; j++) {
				let dist = nodes[i].centroid.distanceTo(nodes[j].centroid);
				edges.push({i, j, distance: dist});
			}
		}

		// Sort by distance ascending
		edges.sort((a, b) => a.distance - b.distance);

		// Union-Find with path compression and rank
		let parent = new Array(n);
		let rank = new Array(n);
		for (let i = 0; i < n; i++) {
			parent[i] = i;
			rank[i] = 0;
		}

		let find = (x) => {
			if (parent[x] !== x) {
				parent[x] = find(parent[x]);
			}
			return parent[x];
		};

		let union = (x, y) => {
			let px = find(x);
			let py = find(y);
			if (px === py) return false;
			if (rank[px] < rank[py]) {
				parent[px] = py;
			} else if (rank[px] > rank[py]) {
				parent[py] = px;
			} else {
				parent[py] = px;
				rank[px]++;
			}
			return true;
		};

		// Greedily add shortest edges
		let mst = [];
		for (let edge of edges) {
			if (union(edge.i, edge.j)) {
				mst.push(edge);
				if (mst.length === n - 1) break;
			}
		}

		return mst;
	}

	update() {
		let camera = this.viewer.scene.getActiveCamera();
		let renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
		let clientWidth = renderAreaSize.width;
		let clientHeight = renderAreaSize.height;

		for (let edge of this.edges) {
			// Scale label based on distance from camera
			let distance = camera.position.distanceTo(
				edge.label.getWorldPosition(new THREE.Vector3())
			);
			let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
			let scale = (70 / pr);
			edge.label.scale.set(scale, scale, scale);

			// Update LineMaterial resolution
			edge.line.material.resolution.set(clientWidth, clientHeight);
		}

		// Scale endpoint dots to constant screen size, clamped to avoid
		// distant dots growing too large in world space
		for (let dot of this.endpointDots) {
			let distance = camera.position.distanceTo(
				dot.getWorldPosition(new THREE.Vector3())
			);
			let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
			let scale = Math.min(7 / pr, distance * 0.008);
			dot.scale.set(scale, scale, scale);
		}
	}

	render() {
		this.viewer.renderer.render(this.scene, this.viewer.scene.getActiveCamera());
	}
}
